<!-- SummarySessionReport.vue - Design 1:1 with UI/صفحة تقارير الجلسات/code.html -->
<template>
  <div
    class="summary-session-report max-w-6xl mx-auto px-4 py-12 lg:py-16 min-h-screen transition-colors duration-300"
    style="background-color: #fcfdfc; color: #1e293b; font-family: 'IBM Plex Sans Arabic', sans-serif;"
  >
    <header class="text-center mb-12">
      <h1 class="text-4xl md:text-5xl font-bold mb-2 tracking-tight" style="color: #c4a47c;">تقارير الجلسات</h1>
      <div class="w-24 h-1 mx-auto rounded-full mt-4" style="background-color: rgba(196, 164, 124, 0.3);"></div>
    </header>

    <!-- Filters bar: الجهة (للتحميل)، الجدول، المعلم، التاريخ، تحميل -->
    <section class="mb-10">
      <div
        class="flex flex-col md:flex-row items-center justify-center gap-4 p-6 rounded-[1rem] shadow-sm border border-slate-100 bg-white"
      >
        <div class="w-full md:w-64">
          <select
            v-model="selectedOrganizationId"
            class="w-full h-12 px-4 rounded-lg border bg-transparent transition-all text-right"
            style="border-color: rgba(139, 160, 142, 0.3); color: #475569;"
            @focus="$event.target.style.outline = 'none'; $event.target.style.boxShadow = '0 0 0 2px #8ba08e';"
            @blur="$event.target.style.boxShadow = ''"
          >
            <option value="">اختر الجهة</option>
            <option v-for="org in organizations" :key="org.id" :value="org.id">{{ org.name }}</option>
          </select>
        </div>
        <div class="w-full md:w-64">
          <select
            v-model="selectedScheduleId"
            @change="onScheduleChange"
            class="w-full h-12 px-4 rounded-lg border bg-transparent transition-all text-right"
            style="border-color: rgba(139, 160, 142, 0.3); color: #475569;"
            @focus="$event.target.style.outline = 'none'; $event.target.style.boxShadow = '0 0 0 2px #8ba08e';"
            @blur="$event.target.style.boxShadow = ''"
          >
            <option value="">اختر الجدول</option>
            <option v-for="sch in scheduleOptions" :key="sch.id" :value="sch.id">{{ sch.title }}</option>
          </select>
        </div>
        <div class="w-full md:w-64">
          <select
            v-model="selectedTeacherId"
            @change="handleFilterChange"
            :disabled="!selectedScheduleId"
            class="w-full h-12 px-4 rounded-lg border bg-transparent transition-all text-right disabled:opacity-60"
            style="border-color: rgba(139, 160, 142, 0.3); color: #475569;"
            @focus="$event.target.style.outline = 'none'; $event.target.style.boxShadow = '0 0 0 2px #8ba08e';"
            @blur="$event.target.style.boxShadow = ''"
          >
            <option value="">كل المعلمين</option>
            <option v-for="t in teacherOptions" :key="t.id" :value="t.id">{{ t.name }}</option>
          </select>
        </div>
        <div class="w-full md:w-56">
          <input
            v-model="selectedDate"
            type="date"
            @change="handleFilterChange"
            class="w-full h-12 px-4 rounded-lg border bg-transparent transition-all"
            style="border-color: rgba(139, 160, 142, 0.3); color: #475569;"
            @focus="$event.target.style.outline = 'none'; $event.target.style.boxShadow = '0 0 0 2px #8ba08e';"
            @blur="$event.target.style.boxShadow = ''"
          />
        </div>
        <button
          v-if="selectedScheduleId && !loading"
          type="button"
          @click="downloadPDF"
          class="flex items-center justify-center w-full md:w-14 h-12 rounded-lg text-white shadow-md transition-transform active:scale-95 hover:opacity-90"
          style="background-color: #8ba08e; box-shadow: 0 4px 6px -1px rgba(139, 160, 142, 0.2);"
          title="تحميل PDF"
        >
          <span class="material-icons-round">download</span>
        </button>
      </div>
    </section>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-8">
      <div
        class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
        style="border-color: #8ba08e;"
      ></div>
      <p class="mt-4 text-lg" style="color: #8ba08e;">جاري التحميل...</p>
    </div>

    <!-- Error -->
    <div
      v-if="error"
      class="bg-red-100 border-2 border-red-400 text-red-700 px-6 py-4 rounded-lg text-right mb-4"
    >
      <strong class="font-bold ml-2">خطأ!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Report content: 4 summary boxes + apologies (1:1 with code.html) -->
    <div v-if="report && !loading" class="summary-report" dir="rtl">
      <!-- PDF-only header (hidden on screen) -->
      <div class="pdf-header" style="display: none;">
        <div class="header-top">
          <div class="header-logos">
            <div class="logo-left">
              <img :src="appLogo" alt="شعار التطبيق" crossorigin="anonymous" />
            </div>
            <div class="logo-right">
              <img
                v-if="selectedOrganization && selectedOrganization.logoUrl"
                :src="selectedOrganization.logoUrl"
                :alt="selectedOrganization.name"
                crossorigin="anonymous"
                @error="onOrgLogoError"
              />
              <img
                v-else-if="selectedOrganization && selectedOrganization.logoBase64"
                :src="`data:image/jpeg;base64,${selectedOrganization.logoBase64}`"
                :alt="selectedOrganization.name"
                crossorigin="anonymous"
                @error="onOrgLogoError"
              />
              <div v-else-if="selectedOrganization" class="logo-placeholder">
                <i class="fas fa-building"></i>
                <span class="placeholder-text">{{ selectedOrganization.name.charAt(0) }}</span>
              </div>
              <div v-else class="logo-placeholder">
                <i class="fas fa-building"></i>
              </div>
            </div>
          </div>
          <div class="header-divider"></div>
        </div>
        <div class="header-content">
          <h2 class="report-title">تقرير الجلسات</h2>
          <div class="report-info">
            <div class="info-row">
              <span class="info-label">التاريخ:</span>
              <span class="info-value">{{ formattedDate }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">الجدول:</span>
              <span class="info-value">{{ selectedScheduleTitle }}</span>
            </div>
          </div>
        </div>
        <div class="header-separator"></div>
      </div>

      <section class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div
          class="p-8 rounded-[1rem] shadow-lg flex flex-col items-center justify-center text-center transform hover:-translate-y-1 transition-all"
          style="background-color: #8ba08e; box-shadow: 0 10px 15px -3px rgba(139, 160, 142, 0.1);"
        >
          <span class="text-white/80 text-xl font-medium mb-4">المراجعة</span>
          <span class="text-white text-5xl font-bold leading-none">{{ report.totalReview ?? 0 }}</span>
        </div>
        <div
          class="p-8 rounded-[1rem] shadow-lg flex flex-col items-center justify-center text-center transform hover:-translate-y-1 transition-all"
          style="background-color: #8ba08e; box-shadow: 0 10px 15px -3px rgba(139, 160, 142, 0.1);"
        >
          <span class="text-white/80 text-xl font-medium mb-4">الحفظ</span>
          <span class="text-white text-5xl font-bold leading-none">{{ report.totalMemorization ?? 0 }}</span>
        </div>
        <div
          class="p-8 rounded-[1rem] shadow-lg flex flex-col items-center justify-center text-center transform hover:-translate-y-1 transition-all"
          style="background-color: #8ba08e; box-shadow: 0 10px 15px -3px rgba(139, 160, 142, 0.1);"
        >
          <span class="text-white/80 text-xl font-medium mb-4">مجموع الأوجه</span>
          <span class="text-white text-5xl font-bold leading-none">{{ report.totalPages ?? 0 }}</span>
        </div>
        <div
          class="p-8 rounded-[1rem] shadow-lg flex flex-col items-center justify-center text-center transform hover:-translate-y-1 transition-all"
          style="background-color: #8ba08e; box-shadow: 0 10px 15px -3px rgba(139, 160, 142, 0.1);"
        >
          <span class="text-white/80 text-xl font-medium mb-4">مجموع الأجزاء</span>
          <span class="text-white text-5xl font-bold leading-none">{{ totalPartsDisplay }}</span>
        </div>
      </section>

      <section>
        <div
          class="w-full p-10 border-2 rounded-[1rem] shadow-sm flex flex-col items-center justify-center text-center transition-all hover:shadow-md bg-white no-break"
          style="border-color: rgba(139, 160, 142, 0.2);"
        >
          <div class="flex flex-col sm:flex-row items-center gap-4 text-2xl md:text-3xl font-bold">
            <span class="text-slate-700">إجمالي الاعتذارات و الغياب:</span>
            <span class="text-4xl md:text-5xl" style="color: #c4a47c;">{{ report.totalApologies ?? 0 }}</span>
          </div>
        </div>
      </section>

      <div class="pdf-bottom-space" style="height: 100px;"></div>
    </div>

    <div v-else-if="!loading && !error" class="text-center py-12 text-slate-500 text-lg">
      الرجاء اختيار الجدول لعرض التقرير.
    </div>
  </div>
</template>

<script>
import url from '@/url';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import appLogo from '@/assets/logo.png';
import { listIqraSchedules, getIqraSchedule } from '@/services/iqraSchedules';

export default {
  data() {
    return {
      organizations: [],
      scheduleOptions: [],
      scheduleDetail: null,
      selectedOrganizationId: '',
      selectedScheduleId: '',
      selectedTeacherId: '',
      selectedDate: new Date().toISOString().split('T')[0],
      report: null,
      loading: false,
      error: null,
      appLogo,
    };
  },
  computed: {
    selectedOrganization() {
      return this.organizations.find((org) => org.id == this.selectedOrganizationId);
    },
    selectedScheduleTitle() {
      if (!this.selectedScheduleId) return '';
      const sch = this.scheduleOptions.find((s) => String(s.id) === String(this.selectedScheduleId));
      return sch?.title || this.scheduleDetail?.title || '';
    },
    teacherOptions() {
      if (!this.scheduleDetail) return [];
      const seen = new Set();
      const teachers = [];
      const add = (id, name) => {
        const key = String(id);
        if (id && !seen.has(key)) {
          seen.add(key);
          teachers.push({ id: Number(id), name: name || `معلم #${id}` });
        }
      };
      for (const t of this.scheduleDetail.teachers || []) {
        if (t.is_active === false) continue;
        const teacher = t.teacher || t;
        add(teacher.id, teacher.full_name || teacher.fullName);
      }
      for (const day of this.scheduleDetail.days || []) {
        if (day.is_active === false) continue;
        for (const slot of day.slots || []) {
          if (slot.is_active === false) continue;
          for (const st of slot.slot_teachers || []) {
            if (st.is_active === false) continue;
            const t = st.teacher || {};
            add(st.teacher_id, t.full_name || t.fullName);
          }
        }
      }
      return teachers;
    },
    totalPartsDisplay() {
      const parts = this.report?.totalParts;
      if (parts != null && typeof parts === 'number') return parts.toFixed(2);
      if (this.report?.totalPages != null) return (Number(this.report.totalPages) / 20).toFixed(2);
      return '0.00';
    },
    formattedDate() {
      if (!this.selectedDate) return '';
      const d = new Date(this.selectedDate);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
  },
  methods: {
    async fetchOrganizations() {
      try {
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.organizations, {
          headers: { 'X-Authorization': localStorage.getItem('token') },
        });
        if (response.data?.result?.data) {
          this.organizations = response.data.result.data;
        }
      } catch (err) {
        console.error('Error fetching organizations:', err);
      }
    },
    async loadScheduleOptions() {
      try {
        const result = await listIqraSchedules({ is_active: true, page: 1, size: 100 });
        const data = result?.data ?? [];
        this.scheduleOptions = (Array.isArray(data) ? data : []).map((s) => ({
          id: String(s.id),
          title: s.title || `جدول #${s.id}`,
        }));
      } catch (err) {
        this.error = err?.message || 'حدث خطأ أثناء تحميل الجداول';
        console.error('Error loading schedules:', err);
      }
    },
    async onScheduleChange() {
      this.scheduleDetail = null;
      this.selectedTeacherId = '';
      this.clearReport();
      if (!this.selectedScheduleId) return;
      try {
        this.scheduleDetail = await getIqraSchedule(Number(this.selectedScheduleId));
      } catch (err) {
        this.error = err?.message || 'حدث خطأ أثناء تحميل تفاصيل الجدول';
        console.error('Error loading schedule detail:', err);
        return;
      }
      await this.fetchReport();
    },
    handleFilterChange() {
      if (!this.selectedScheduleId) return;
      this.fetchReport();
    },
    clearReport() {
      this.report = null;
      this.error = null;
    },
    async fetchReport() {
      if (!this.selectedScheduleId) return;
      this.loading = true;
      this.error = null;
      try {
        const params = {
          scheduleId: Number(this.selectedScheduleId),
          date: this.selectedDate,
        };
        if (this.selectedTeacherId) params.teacherId = Number(this.selectedTeacherId);
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.iqra_summary_report, {
          params,
          headers: { 'X-Authorization': localStorage.getItem('token') },
        });
        if (response.data?.result?.data) {
          this.report = response.data.result.data;
        }
      } catch (err) {
        this.error = err.response?.data?.result?.mesAr || err.response?.data?.mesAr || err?.message || 'حدث خطأ أثناء تحميل التقرير';
        console.error('Error fetching report:', err);
      } finally {
        this.loading = false;
      }
    },
    onOrgLogoError(event) {
      event.target.style.display = 'none';
    },
    async downloadPDF() {
      try {
        const pdfHeader = document.querySelector('.pdf-header');
        if (pdfHeader) pdfHeader.style.display = 'block';
        await new Promise((r) => setTimeout(r, 500));

        const element = document.querySelector('.summary-report');
        const scheduleName = this.selectedScheduleTitle ? this.selectedScheduleTitle.replace(/[^\u0600-\u06FF\w\s]/g, '') : 'جدول';
        const orgName = this.selectedOrganization ? this.selectedOrganization.name.replace(/[^\u0600-\u06FF\w\s]/g, '') : '';
        const baseFilename = `تقرير_الجلسات_${scheduleName}_${this.selectedDate}${orgName ? '_' + orgName : ''}`;
        const safeFilename = baseFilename.replace(/[^\u0600-\u06FF\w\s-]/g, '').trim() + '.pdf';

        element.style.height = 'auto';
        element.style.minHeight = 'auto';
        element.style.overflow = 'visible';
        element.style.width = '210mm';
        element.style.maxWidth = '210mm';
        element.style.padding = '20px';
        element.style.boxSizing = 'border-box';
        await new Promise((r) => setTimeout(r, 300));

        const opt = {
          margin: [0.2, 0.2, 0.2, 0.2],
          filename: safeFilename,
          image: { type: 'jpeg', quality: 1.0, crossOrigin: 'anonymous' },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            letterRendering: true,
            logging: false,
            width: 794,
            height: element.scrollHeight,
            scrollX: 0,
            scrollY: 0,
            backgroundColor: '#ffffff',
            windowWidth: 794,
            windowHeight: element.scrollHeight,
            onclone(clonedDoc) {
              const el = clonedDoc.querySelector('.summary-report');
              if (el) {
                el.style.height = 'auto';
                el.style.minHeight = 'auto';
                el.style.overflow = 'visible';
                el.style.transform = 'none';
                el.style.width = '100%';
                el.style.maxWidth = '100%';
                el.style.padding = '20px';
                el.style.boxSizing = 'border-box';
              }
            },
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'], avoid: '.no-break' },
        };

        element.classList.add('pdf-export');
        const pdfBlob = await html2pdf().set(opt).from(element).outputPdf('blob');
        const blobUrl = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = safeFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        element.classList.remove('pdf-export');
        element.style.width = '';
        element.style.maxWidth = '';
        element.style.padding = '';
        element.style.boxSizing = '';
        if (pdfHeader) pdfHeader.style.display = 'none';
      } catch (error) {
        console.error('Error generating PDF:', error);
        this.error = 'حدث خطأ أثناء تحميل الملف';
        const el = document.querySelector('.summary-report');
        const h = document.querySelector('.pdf-header');
        if (el) el.classList.remove('pdf-export');
        if (h) h.style.display = 'none';
      }
    },
  },
  created() {
    this.fetchOrganizations();
    this.loadScheduleOptions();
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@300;400;500;600;700&display=swap');
@import url('https://fonts.googleapis.com/icon?family=Material+Icons+Round');

.summary-session-report {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.material-icons-round {
  font-family: 'Material Icons Round';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
}
input[type='date']::-webkit-calendar-picker-indicator {
  cursor: pointer;
  filter: invert(0.5);
}

/* PDF header (hidden on screen, used in PDF export) */
.pdf-header {
  margin-bottom: 40px;
  direction: rtl;
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 2px solid #8ba08e;
  border-radius: 1rem;
  box-shadow: 0 8px 16px rgba(139, 160, 142, 0.1);
}
.header-top {
  margin-bottom: 25px;
}
.header-logos {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  height: 100px;
  padding: 0 20px;
}
.logo-left img,
.logo-right img {
  max-height: 100px;
  max-width: 150px;
  height: auto;
  width: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.logo-placeholder {
  height: 100px;
  width: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #8ba08e 0%, #7a8b7e 100%);
  border-radius: 8px;
  color: white;
  font-size: 30px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.placeholder-text {
  font-size: 24px;
  font-weight: bold;
  margin-top: 5px;
}
.header-divider {
  height: 3px;
  background: linear-gradient(90deg, #8ba08e 0%, #7a8b7e 50%, #8ba08e 100%);
  border-radius: 2px;
  margin: 0 20px;
}
.header-content {
  text-align: center;
}
.report-title {
  font-size: 36px;
  font-weight: bold;
  color: #c4a47c;
  margin-bottom: 20px;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
}
.report-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}
.info-row {
  display: flex;
  align-items: center;
  gap: 15px;
  background: rgba(139, 160, 142, 0.1);
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid rgba(139, 160, 142, 0.3);
}
.info-label {
  font-size: 18px;
  font-weight: bold;
  color: #8ba08e;
  min-width: 60px;
}
.info-value {
  font-size: 18px;
  color: #333;
  direction: rtl;
  unicode-bidi: bidi-override;
}
.header-separator {
  height: 4px;
  background: linear-gradient(90deg, transparent 0%, #8ba08e 20%, #7a8b7e 50%, #8ba08e 80%, transparent 100%);
  margin-top: 20px;
  border-radius: 2px;
}

.pdf-export {
  direction: rtl;
  padding: 20px !important;
  background: white;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 100% !important;
  box-sizing: border-box !important;
}
.pdf-export * {
  font-family: 'IBM Plex Sans Arabic', sans-serif !important;
  direction: rtl !important;
  unicode-bidi: bidi-override !important;
}
.pdf-export .pdf-header {
  display: block !important;
  page-break-after: avoid;
  margin-bottom: 30px;
}
.pdf-export .summary-box,
.pdf-export .apologies-section {
  break-inside: avoid;
  page-break-inside: avoid;
}
</style>
